import { Container, Graphics, Sprite } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { Fx } from '../render/fx';
import { background, frame } from '../assets/loader';
import { dogFrame } from '../assets/names';
import { after, button, label, roundedPanel } from '../ui/components';
import { sound } from '../audio/sound';
import { haptic } from '../audio/haptics';

/** Mockup 31: postcard "Welcome to Paris!" with Coco in front of a Paris scene. */
export class LocationUnlockScreen implements Screen {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly card = new Container();
  private readonly fx = new Fx();
  private readonly explore: Container;
  private l?: Layout;

  constructor(onExplore: () => void) {
    const w = 820;
    const h = 1080;
    this.card.addChild(roundedPanel(w, h, { radius: 36 }));
    this.card.pivot.set(w / 2, h / 2);
    const title = label('Welcome to\nParis!', FONT.size.celebration + 16, { weight: FONT.weight.heading });
    title.style.align = 'center';
    title.position.set(w / 2 - 20, 150);
    // stamp with a paw, top right
    const stamp = new Container();
    stamp.addChild(new Graphics().roundRect(-52, -60, 104, 120, 10).fill(COLORS.blush).stroke({ width: SHAPE.outline, color: COLORS.cocoa }));
    const paw = new Sprite(frame('obstacle_paw_stain'));
    paw.anchor.set(0.5);
    paw.scale.set(64 / paw.texture.width);
    paw.tint = COLORS.cocoa;
    stamp.addChild(paw);
    stamp.position.set(w - 110, 120);
    stamp.rotation = 0.12;
    // picture: Paris backdrop cropped into the card, Coco in front
    const pic = new Container();
    const pw = w - 120;
    const ph = 640;
    const scene = new Sprite(background('bg_gameplay_paris'));
    const sc = pw / scene.texture.width;
    scene.scale.set(sc);
    scene.y = -(scene.texture.height * sc - ph) * 0.45;
    const mask = new Graphics().roundRect(0, 0, pw, ph, 24).fill(0xffffff);
    scene.mask = mask;
    const coco = new Sprite(frame(dogFrame('coco', 'happy')));
    coco.anchor.set(0.5, 1);
    coco.scale.set(460 / coco.texture.height);
    coco.position.set(pw / 2, ph - 20);
    pic.addChild(scene, mask, coco, new Graphics().roundRect(0, 0, pw, ph, 24).stroke({ width: SHAPE.outline, color: COLORS.cocoa }));
    pic.position.set(60, 290);
    const meet = label('Meet Coco', FONT.size.screenTitle, { weight: FONT.weight.heading });
    meet.position.set(w / 2, 1000);
    this.card.addChild(title, stamp, pic, meet);
    this.explore = button('Explore Paris', 600, 'primary', onExplore, 104);
    this.view.addChild(this.bg, this.card, this.fx.view, this.explore);
  }

  destroy(): void {
    this.fx.destroy();
  }

  layout(l: Layout): void {
    const first = !this.l;
    this.l = l;
    this.bg.clear().rect(l.left, 0, l.right - l.left, l.bottom).fill(COLORS.mint);
    const avail = l.safeBottom - l.safeTop - 180;
    const s = Math.min(1, avail / 1080);
    this.card.scale.set(s);
    this.card.position.set(SCREEN.width / 2, l.safeTop + 20 + (1080 * s) / 2);
    this.explore.position.set(SCREEN.width / 2, l.safeBottom - 70);
    if (first) {
      this.card.rotation = -0.2;
      this.card.alpha = 0;
      gsap.to(this.card, { rotation: -0.025, alpha: 1, duration: 0.6, ease: 'back.out(1.4)' });
      after(this.view, 450, () => {
        sound.play('win');
        haptic('success');
        this.fx.confetti(l.left, l.right - l.left, 0, 70);
        for (const x of [120, 900]) this.fx.burst(x, l.safeTop + 200, { frame: 'vfx_sparkle', count: 5, speed: 200, gravity: 0, size: 40 });
      });
    }
  }
}
